import type { App, InjectionKey } from 'vue'
import type { ProTableDensity, ProTableTool } from '@/components/FaProTable/types'

export interface AppProTableDefaults {
  /**
   * 表格密度。
   * 支持值：
   * - `compact`：紧凑模式，对应更小的行高
   * - `default`：默认模式
   * - `comfortable`：宽松模式，对应更大的行高
   */
  density: ProTableDensity
  /**
   * 是否显示表格边框。
   * 支持值：
   * - `true`：显示边框
   * - `false`：隐藏边框
   */
  bordered: boolean
  /**
   * 是否显示斑马纹。
   * 支持值：
   * - `true`：开启奇偶行斑马纹
   * - `false`：关闭斑马纹
   */
  striped: boolean
  /**
   * 工具栏默认启用的内建工具。
   * 支持值：
   * - `refresh`：刷新
   * - `density`：密度切换
   * - `fullscreen`：全屏
   * - `print`：打印
   * - `settings`：列设置与展示设置
   */
  tools: ProTableTool[]
  /**
   * 是否默认显示工具栏。
   * 支持值：
   * - `true`：显示工具栏
   * - `false`：隐藏工具栏
   */
  showToolbar: boolean
  /**
   * 是否默认显示分页器。
   * 支持值：
   * - `true`：显示分页器
   * - `false`：隐藏分页器
   */
  showPagination: boolean
  /**
   * 选择列默认宽度，单位为像素。
   * 支持值：
   * - 任意大于 0 的数字，例如 `52`
   */
  selectionWidth: number
  /**
   * 序号列默认宽度，单位为像素。
   * 支持值：
   * - 任意大于 0 的数字，例如 `60`
   */
  indexWidth: number
}

export const defaultProTableDefaults: Readonly<AppProTableDefaults> = Object.freeze({
  density: 'default',
  bordered: false,
  striped: false,
  tools: ['refresh', 'density', 'fullscreen', 'print', 'settings'] as ProTableTool[],
  showToolbar: true,
  showPagination: true,
  selectionWidth: 52,
  indexWidth: 60,
})

export const proTableDefaultsInjectionKey: InjectionKey<Readonly<AppProTableDefaults>> = Symbol('app-pro-table-defaults')

export function installProTableDefaults(app: App, defaults: Partial<AppProTableDefaults> = {}) {
  app.provide(proTableDefaultsInjectionKey, Object.freeze({
    ...defaultProTableDefaults,
    ...defaults,
  }))
}
